import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RulesEngineFormValues,
  RulesEngineSourceType,
  TriggerSource,
} from '../rules-engine-form-page';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import {
  Folder,
  ResourceCalendar,
  SharedDriveModel,
} from '../../../../../types';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { UserKeys } from '../../../../api/users-api';
import UsersAutocomplete from '../../../../shared/users-autocomplete';
import GroupsAutocomplete from '../../../../shared/groups-autocomplete';
import DomainsAutoComplete from '../../../../shared/domain-autocomplete';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import SharedDrivesAutocomplete from '../../../../shared/shared-drives-autocomplete';
import SharedDrivesBigQueryAutocomplete from '../../../../shared/shared-drive-bigquery-autocomplete';
import AvatarChip from '../../../../shared/avatar-chip';
import DriveTreeDialog from '../steps-old/drive-tree-dialog';
import ResourceCalendarsAutoComplete from '../../../../shared/resource-calendars-autocomplete';
import {
  triggerEventOptions,
  triggerGroupUiOptions,
  getEventUiGroup,
  TriggerEventOption,
} from '../trigger-event-options';
import { RuleModel } from '../../../../../types';
import StatusLabel, { StatusType } from '../../../../shared/status-label';
import z from 'zod';
import SharedGroupedSelect from '../../../../shared/shared-grouped-select';
import AlertCard from '../../../../shared/alert-card';
import BoxedIcon from '../../../../shared/boxed-icon';
import SubsectionHeader from '../../../../shared/subsection-header';
import AllSourceOptions from './all-source-options';

const TriggerSourceSchema = z.object({
  type: z.string(),
  value: z.string(),
  driveFolderOwner: z.string().optional(),
  driveFolderId: z.string().optional(),
  driveFolderName: z.string().optional(),
  selectedSharedDriveId: z.string().optional(),
});

const DriveFolderSourceSchema = z.object({
  driveFolderId: z.string(),
  driveFolderOwner: z.string(),
});

export const RulesEngineWhenStepSchema = z
  .object({
    triggerInfo: z.object({
      triggerGroup: z.string(),
      triggerEvent: z.string().min(1, 'Required field.'),
      occursEverytime: z.boolean(),
      triggerThreshold: z
        .number({
          error: 'Required field.',
        })
        .refine((val) => val === undefined || val > 0, {
          message: 'Must be greater than 0.',
        }),
      hasTimeDuration: z.boolean(),
      triggerDuration: z
        .number({
          error: 'Required field.',
        })
        .refine((val) => val === undefined || val > 0, {
          message: 'Must be greater than 0.',
        }),
      triggerDurationType: z.string(),
      triggerDomainSources: z.array(z.string()),
      triggerGroupSources: z.array(z.string()),
      triggerOrgSources: z.array(z.string()),
      triggerUserSources: z.array(z.string()),
      driveRegExs: z.array(z.string()),
      sharedDriveIds: z.array(z.string()),
      driveFolderTriggerSources: z.array(DriveFolderSourceSchema),
    }),
    triggerSources: z.array(TriggerSourceSchema).optional(),
    allSource: z.boolean().optional(),
    targetsExcluded: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      (data.triggerInfo.triggerGroup !== 'calendar' ||
        data.triggerInfo.triggerEvent == 'change_calendar_acls') &&
      (!data.triggerSources || data.triggerSources.length <= 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one trigger source is required.',
        path: ['triggerSources'],
      });
    }
  });

interface RulesEngineWhenStepProps {
  control: Control<RulesEngineFormValues>;
  errors: FieldErrors<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  sourceType: RulesEngineSourceType;
  setSourceType: Dispatch<SetStateAction<RulesEngineSourceType | null>>;
  watch: UseFormWatch<RulesEngineFormValues>;
  trigger: UseFormTrigger<RulesEngineFormValues>;
  domains: string[];
  setSelectedRule: (rule: RuleModel) => void;
  allRules: RuleModel[];
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
  isPersonalCalendar: boolean;
  resourceCalendars: ResourceCalendar[];
}

export default function RulesEngineWhenStep({
  control,
  errors,
  setValue,
  watch,
  trigger,
  sourceType,
  setSourceType,
  domains,
  setSelectedRule,
  allRules,
  clearErrors,
  isPersonalCalendar,
  resourceCalendars,
}: RulesEngineWhenStepProps) {
  const theme = useTheme();
  const triggerGroupOptions = triggerGroupUiOptions;

  const durationTypeOptions = [
    { value: 'minutes', displayValue: 'Minutes' },
    { value: 'hours', displayValue: 'Hours' },
    { value: 'days', displayValue: 'Days' },
  ];

  const occursEverytime = watch('triggerInfo.occursEverytime');
  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');
  const hasTimeDuration = watch('triggerInfo.hasTimeDuration');
  const allSource = watch('allSource');

  const selectedTriggerGroup = getEventUiGroup(triggerEvent);

  const entityList = useMemo(() => {
    const baseList = ['User', 'Group', 'Orgs', 'Domain'];
    if (triggerGroup === 'calendar' && !isPersonalCalendar) {
      return [...baseList, 'Resource Calendar'];
    }
    return baseList;
  }, [triggerGroup, triggerEvent, isPersonalCalendar]);

  if (allSource && sourceType !== 'User') {
    // When editing a rule where allSource is true, ensure sourceType is 'User'.
    setSourceType('User');
  }

  const [regex, setRegex] = useState('');
  const queryClient = useQueryClient();
  const [hasRegexError, setHasRegexError] = useState(false);
  const [sharedDrivesAutoCompleteValue, setSharedDrivesAutoCompleteValue] =
    useState('');
  const [userDriveAutoCompleteValue, setUserDriveAutoCompleteValue] =
    useState('');
  const [openDriveTreeDialog, setOpenDriveTreeDialog] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('');
  const { currentTier } = useUserSettings();

  const currentTriggerEventOption =
    triggerEventOptions.find((event) => event.value === triggerEvent) ||
    triggerEventOptions[0];

  const isDriveUserEvent =
    triggerGroup === 'drive' &&
    currentTriggerEventOption.type === 'drive' &&
    sourceType === 'User';

  const currentRuleId = watch('id');
  const similarRules = useMemo(() => {
    if (!allRules || !triggerGroup || !triggerEvent) {
      return [];
    }
    return allRules.filter(
      (rule) =>
        rule.id !== currentRuleId &&
        rule.triggerInfo.triggerGroup === triggerGroup &&
        rule.triggerInfo.triggerEvent === triggerEvent
    );
  }, [allRules, triggerGroup, triggerEvent, currentRuleId]);

  const triggerEventDisplay =
    triggerEventOptions.find((o) => o.value === triggerEvent)?.displayValue ||
    '';

  const indexOfCurrentEntity = entityList.indexOf(
    currentTriggerEventOption.lowestEntityAllowed
  );
  const allowedEntityList = entityList.slice(indexOfCurrentEntity);

  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const autocompleteKey = useRef(new Date().getTime().toString());

  const [driveEntityType, setDriveEntityType] = useState<
    'Shared Drives' | 'User Drive' | 'Regular Expressions'
  >('Shared Drives');

  const { fields, remove, append, replace } = useFieldArray({
    control,
    name: 'triggerSources',
  });

  const sortedTriggerEventOptions = [...triggerEventOptions].sort((a, b) =>
    a.group && b.group
      ? a.group.localeCompare(b.group)
      : a.type.localeCompare(b.type)
  );

  function addSelection(source: TriggerSource) {
    const currentSources = watch('triggerSources') ?? [];
    const wasEmpty = (currentSources.length ?? 0) === 0;

    append(source);

    if (wasEmpty) {
      trigger('triggerSources');
    }

    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponse('');
    setSharedDrivesAutoCompleteValue('');
    setRegex('');
    setOpenDriveTreeDialog(false);
    setUserDriveAutoCompleteValue('');
  }

  const getDriveFolderName = useCallback(
    (userEmail: string, folderId: string) => {
      const allFolders = queryClient.getQueryData<
        InfiniteData<Folder[], unknown> | undefined
      >([UserKeys.DriveFolders, userEmail]);

      const folders = allFolders?.pages.reduce(
        (acc, page) => acc.concat(page),
        []
      );

      const folder = folders?.find((folder) => folder.id === folderId);

      return folder?.title || '';
    },
    [queryClient]
  );

  function checkIfExisting(source: TriggerSource) {
    if (!source.value) {
      return true;
    }
    const currentSources = watch('triggerSources') ?? [];

    return currentSources.some((existing) => {
      if (existing.type !== source.type) {
        return false;
      }

      if (source.type === 'User Drive') {
        return (
          existing.driveFolderOwner === source.driveFolderOwner &&
          existing.driveFolderId === source.driveFolderId
        );
      }

      return existing.value === source.value;
    });
  }

  function resetDriveFilters() {
    setValue('hasFileTypeFilter', false);
    setValue('hasFileSizeFilter', false);
    setValue('triggerInfo.fileSharedToSpecificUser', []);
    setValue('hasSelectedDomainSharing', false);
    setValue('sizeComparison', 'larger');
    setValue('triggerInfo.canFindInSearchResults', false);
    setValue('triggerInfo.fileMimeTypes', []);
    setValue('triggerInfo.driveFileSizeCeiling', 0);
    setValue('triggerInfo.driveFileSizeFloor', 0);
    setValue('sizeType', undefined);
    setValue('hasSharingPermissionsFilter', false);
    setValue('selectedSharingOption', undefined);
    setValue('triggerInfo.fileIsSharedToDomain', false);
    setValue('triggerInfo.fileIsSharedPublic', false);
    setValue('triggerInfo.fileSharedToUserExternalToDomain', false);
    setValue('triggerInfo.fileSharedToUserInternalToDomain', false);
    setValue('triggerInfo.mustHaveLinkToAccess', false);
    setValue('hasSharingPermissionsFilter', false);
    clearErrors([
      'triggerInfo.fileSharedToSpecificUser',
      'triggerInfo.canFindInSearchResults',
      'triggerInfo.fileMimeTypes',
      'triggerInfo.driveFileSizeCeiling',
      'triggerInfo.driveFileSizeFloor',
      'triggerInfo.fileIsSharedToDomain',
      'triggerInfo.fileIsSharedPublic',
      'triggerInfo.fileSharedToUserExternalToDomain',
      'triggerInfo.fileSharedToUserInternalToDomain',
      'triggerInfo.mustHaveLinkToAccess',
    ]);
  }

  function resetAdminFilters() {
    setValue('hasProfileFieldFilter', false);
    setValue('profileFieldFilter.profileFieldType', undefined);
    setValue('profileFieldFilter.profileFieldValue', undefined);
    setValue('profileFieldFilter.profileFieldRegex', undefined);
    setValue('profileFieldFilter.profileFieldValueExact', false);
    setValue('groupInfo.groupEmail', []);
    setValue('groupInfo.groupRole', []);
    clearErrors([
      'profileFieldFilter.profileFieldType',
      'profileFieldFilter.profileFieldValue',
      'profileFieldFilter.profileFieldRegex',
      'groupInfo.groupEmail',
      'groupInfo.groupRole',
    ]);
  }

  const handleTriggerEventRenderValue = (value: TriggerEventOption | null) => {
    const triggerGroupValue = value
      ? value.group
        ? triggerGroupUiOptions.find((group) => group.value === value.group)
        : triggerGroupUiOptions.find((group) => group.value === value.type)
      : null;

    if (!value && !triggerGroupValue) {
      return null;
    }

    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        {triggerGroupValue?.iconProps ? (
          <BoxedIcon {...triggerGroupValue.iconProps} />
        ) : null}
        <Stack spacing={0}>
          <Typography variant="caption" color="text.secondary">
            {triggerGroupValue?.displayValue ?? ''}
          </Typography>

          <Stack direction="row" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {value?.displayValue ?? ''}
            </Typography>

            <Typography variant="caption" color="text.secondary" noWrap>
              {value?.displayInfoValue}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderAlertCardItem = (rule: RuleModel) => {
    const status = (rule.basicInfo.status?.toUpperCase() as StatusType) ?? null;
    const statusChip = status ? <StatusLabel status={status} /> : null;
    return (
      <Box
        key={rule.id}
        sx={{
          py: 1,
          width: '100%',
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${theme.palette.colors.grey[300]}`,
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            color: theme.palette.colors.neutral[700],
          }}
        >
          <Typography
            fontWeight="bold"
            noWrap
            sx={{ flexGrow: 1, textOverflow: 'ellipsis' }}
          >
            {rule.basicInfo.ruleName}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {statusChip}
            <Link
              component="button"
              variant="body2"
              type="button"
              onClick={() => {
                setSelectedRule(rule);
              }}
            >
              View Rule
            </Link>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const handleFrequencyChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange: (value: boolean) => void
  ) => {
    e.stopPropagation();
    const everyTime = e.target.value === 'everyTime';
    onChange(everyTime);
    if (everyTime) {
      setValue('triggerInfo.triggerThreshold', 1);
      setValue('triggerInfo.triggerDuration', 1);
      setValue('triggerInfo.hasTimeDuration', false);
    }
    clearErrors([
      'triggerInfo.triggerThreshold',
      'triggerInfo.triggerDuration',
      'triggerInfo.triggerDurationType',
      'triggerInfo.hasTimeDuration',
    ]);
  };

  const handleAddAllUsersChecked = (value: boolean) => {
    setValue('allSource', value);

    replace([]);
    setValue('targetsExcluded', []);
    setSourceType('User');
  };

  const handleTriggerEventChange = (
    value: TriggerEventOption | null,
    onChange: (value: string) => void
  ) => {
    const updatedTriggerGroup = value?.type ?? '';
    const updatedTriggerEvent = value?.value ?? '';

    onChange(updatedTriggerEvent);

    setValue('triggerInfo.triggerGroup', updatedTriggerGroup);

    replace([]);
    setValue('triggerInfo.triggerUserSources', []);
    setValue('triggerInfo.triggerGroupSources', []);
    setValue('triggerInfo.triggerOrgSources', []);
    setValue('triggerInfo.triggerDomainSources', []);
    setValue('triggerInfo.sharedDriveIds', []);
    setValue('triggerInfo.driveRegExs', []);
    setValue('triggerInfo.driveFolderTriggerSources', []);
    setValue('allSource', false);
    setValue('targetsExcluded', []);
    setValue('triggerInfo.triggerCalendarSources', []);
    setValue('conditionBlocks', []);

    resetAdminFilters();
    resetDriveFilters();

    setValue('actionInfo.actionTodo', []);

    setSourceType(null);
    if (updatedTriggerGroup && value) {
      setSourceType(value.lowestEntityAllowed as RulesEngineSourceType);
    }
  };

  const handleRegexChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e.stopPropagation();
    const value = e.target.value;

    setRegex(value);

    try {
      new RegExp(value);
      setHasRegexError(false);
    } catch {
      setHasRegexError(true);
    }
  };

  function handleSourceSelection(value: string) {
    if (sourceType === 'Resource Calendar') {
      const currentSources = watch('triggerInfo.triggerCalendarSources') ?? [];
      if (currentSources.includes(value)) {
        return;
      }
      setValue('triggerInfo.triggerCalendarSources', [
        ...currentSources,
        value,
      ]);
      trigger('triggerInfo.triggerCalendarSources');
      autocompleteKey.current = new Date().getTime().toString();
      setAutoCompleteResponse('');

      return;
    }

    const source: TriggerSource = {
      type: sourceType,
      value,
    };

    if (checkIfExisting(source)) {
      return;
    }

    setAutoCompleteResponse(value);
    addSelection(source);
  }

  function handleSharedDriveSelection(drive: SharedDriveModel | null) {
    if (drive) {
      const source: TriggerSource = {
        type: 'Shared Drive',
        value: drive.name,
        selectedSharedDriveId: drive.id,
      };
      if (!checkIfExisting(source)) {
        addSelection({
          type: 'Shared Drive',
          value: drive.name,
          selectedSharedDriveId: drive.id,
        });
      }
    } else return;
  }

  function handleUserDriveSelection(
    selectedDrive: string,
    selectedFolder: string
  ) {
    const source: TriggerSource = {
      type: 'User Drive',
      value: selectedDrive,
      driveFolderOwner: selectedDrive,
      driveFolderId: selectedFolder,
      driveFolderName: getDriveFolderName(
        userDriveAutoCompleteValue,
        selectedFolder
      ),
    };
    if (checkIfExisting(source)) {
      return;
    }
    addSelection(source);
  }

  const renderSourceAutocomplete = (): React.ReactElement => {
    switch (sourceType) {
      case 'User':
        return (
          <UsersAutocomplete
            newKey={`${autocompleteKey.current}user`}
            autocompleteValue={autoCompleteResponse}
            setAutoCompleteValue={handleSourceSelection}
            errors={!!errors.triggerSources}
            helperText={errors?.triggerSources?.message || undefined}
            disabled={allSource}
          />
        );

      case 'Group':
        return (
          <GroupsAutocomplete
            newKey={`${autocompleteKey.current}group`}
            autocompleteValue={autoCompleteResponse}
            setAutoCompleteValue={handleSourceSelection}
            errors={!!errors.triggerSources}
            helperText={errors?.triggerSources?.message || undefined}
          />
        );

      case 'Domain':
        return (
          <DomainsAutoComplete
            newKey={`${autocompleteKey.current}domain`}
            size="medium"
            domains={domains}
            autoCompleteValue={autoCompleteResponse}
            setAutoCompleteValue={handleSourceSelection}
            hasError={!!errors.triggerSources}
            helperText={errors?.triggerSources?.message || undefined}
          />
        );

      case 'Resource Calendar':
        return (
          <ResourceCalendarsAutoComplete
            newKey={`${autocompleteKey.current}resource-calendar`}
            size="medium"
            resourceCalendars={resourceCalendars || []}
            setAutoCompleteValue={handleSourceSelection}
            hasError={!!errors.triggerInfo?.triggerCalendarSources}
            helperText={
              errors?.triggerInfo?.triggerCalendarSources?.message || undefined
            }
            disabled={allSource}
          />
        );

      case 'Orgs':
      default:
        return (
          <OrganizationsAutoComplete
            newKey={`${autocompleteKey.current}org`}
            size="medium"
            autoCompleteValue={autoCompleteResponse}
            setAutoCompleteValue={handleSourceSelection}
            hasError={!!errors.triggerSources}
            helperText={errors?.triggerSources?.message || undefined}
          />
        );
    }
  };

  const renderSourceChips = () => {
    const calendarSources = watch('triggerInfo.triggerCalendarSources') ?? [];

    if (fields.length === 0 && calendarSources.length === 0) {
      return <Box sx={{ flex: 1 }} />;
    }

    const handleDeleteCalendarSource = (sourceToDelete: string) => {
      setValue(
        'triggerInfo.triggerCalendarSources',
        calendarSources.filter((s: string) => s !== sourceToDelete)
      );
      trigger('triggerInfo.triggerCalendarSources');
    };

    const regularChips = fields.map((source, index) => {
      const avatarChipProps = {
        onDelete: () => remove(index),
        size: 'small' as const,
        email: source.value,
        label: source.value,
      };
      switch (source.type) {
        case 'User':
        case 'Group':
        case 'Domain':
          return (
            <AvatarChip
              key={source.id}
              type={source.type}
              {...avatarChipProps}
            />
          );
        case 'Orgs':
          return (
            <AvatarChip key={source.id} type="OrgUnit" {...avatarChipProps} />
          );
        default:
          return null;
      }
    });

    const calendarChips = calendarSources.map((source: string) => {
      const calendar = resourceCalendars?.find(
        (cal) => cal.resourceEmail === source || cal.resourceId === source
      );

      return (
        <AvatarChip
          key={source}
          type="Resource Calendar"
          onDelete={() => handleDeleteCalendarSource(source)}
          size="small"
          email={source}
          label={calendar?.resourceName || source}
        />
      );
    });

    return (
      <>
        {regularChips}
        {calendarChips}
      </>
    );
  };

  const renderDriveSourceSelector = (): React.ReactElement => {
    switch (driveEntityType) {
      case 'Shared Drives': {
        const sharedDriveProps = {
          newKey: autocompleteKey.current,
          size: 'medium' as const,
          autocompleteValue: sharedDrivesAutoCompleteValue,
          onDriveSelect: handleSharedDriveSelection,
          setAutoCompleteValue: setSharedDrivesAutoCompleteValue,
          errors: !!errors.triggerSources,
          helperText: errors?.triggerSources?.root?.message || undefined,
        };

        return (
          <Tooltip
            arrow
            title="This searches for a shared drive whose title contains the word you are typing in. Ex: name contains 'confidential' when typing confidential"
          >
            {currentTier !== 'enterprise' ? (
              <SharedDrivesAutocomplete {...sharedDriveProps} />
            ) : (
              <SharedDrivesBigQueryAutocomplete
                {...sharedDriveProps}
                newKey={`${autocompleteKey.current}sharedDrive`}
              />
            )}
          </Tooltip>
        );
      }

      case 'Regular Expressions':
        return (
          <Stack direction="row" gap={1}>
            <TextField
              fullWidth
              label="Regular Expression"
              autoComplete="off"
              value={regex}
              onChange={handleRegexChange}
              error={hasRegexError}
              helperText={
                hasRegexError ? 'Invalid regular expression.' : undefined
              }
              placeholder="^[a-zA-Z0-9_]+\\.txt$"
            />

            <Button
              sx={{ height: 40, textTransform: 'none' }}
              variant="contained"
              onClick={() => {
                addSelection({
                  type: 'RegEx',
                  value: regex,
                });
                setHasRegexError(false);
              }}
              disabled={regex.length === 0 || hasRegexError}
            >
              Add
            </Button>
          </Stack>
        );

      case 'User Drive':
      default:
        return (
          <UsersAutocomplete
            newKey={`${autocompleteKey.current}user`}
            autocompleteValue={userDriveAutoCompleteValue}
            setAutoCompleteValue={(value) => {
              setUserDriveAutoCompleteValue(value);
              setOpenDriveTreeDialog(true);
            }}
            errors={!!errors.triggerSources}
            helperText={errors?.triggerSources?.root?.message || undefined}
          />
        );
    }
  };

  const renderDriveSourceChips = () => {
    if (fields.length === 0) {
      return <Box sx={{ flex: 1 }} />;
    }

    return fields.map((source, index) => {
      const avatarChipProps = {
        onDelete: () => remove(index),
        size: 'small' as const,
      };

      if (source.type === 'User Drive') {
        return (
          <AvatarChip
            key={source.id}
            {...avatarChipProps}
            email={source.driveFolderOwner || ''}
            type="User Drive"
            label={`${source.driveFolderOwner}: ${source.driveFolderName}`}
          />
        );
      }

      if (source.type === 'Shared Drive' || source.type === 'RegEx') {
        return (
          <AvatarChip
            key={source.id}
            {...avatarChipProps}
            email={source.value}
            type={source.type}
            label={source.value}
          />
        );
      }

      return null;
    });
  };

  return (
    <Stack width={'100%'} gap={3} divider={<Divider flexItem />}>
      <Stack gap={2}>
        <SubsectionHeader
          title="Trigger Event *"
          description="The action in Google Workspace that starts this rule."
        />

        <Controller
          name="triggerInfo.triggerEvent"
          control={control}
          render={({ field, fieldState }) => (
            <SharedGroupedSelect
              options={sortedTriggerEventOptions}
              groupBy={(option) =>
                option.group
                  ? triggerGroupUiOptions.find(
                      (group) => group.value === option.group
                    )?.displayValue ?? option.group
                  : triggerGroupUiOptions.find(
                      (group) => group.value === option.type
                    )?.displayValue ?? option.type
              }
              getOptionLabel={(option) => option.displayValue}
              getOptionDescription={(option) => option.displayInfoValue ?? ''}
              getOptionTags={(option) =>
                option.tags?.map((tag) => ({
                  label: tag,
                  variant: tag === 'New' ? 'green' : 'blue',
                })) ?? []
              }
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              value={
                triggerEventOptions.find(
                  (option) => option.value === field.value
                ) ?? null
              }
              onChange={(_, value) =>
                handleTriggerEventChange(value, field.onChange)
              }
              placeholder="Select a trigger event"
              renderValue={(value) => handleTriggerEventRenderValue(value)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        {similarRules.length > 0 && (
          <AlertCard
            severity="warning"
            items={similarRules}
            itemsThresholdBeforeScroll={4}
            message={
              <Typography variant="body2">
                <span>
                  We found {similarRules.length}{' '}
                  {similarRules.length === 1 ? 'similar rule' : 'similar rules'}{' '}
                  already targeting '
                  {triggerGroupOptions.find(
                    (o) => o.value === selectedTriggerGroup
                  )?.displayValue || triggerGroup}{' '}
                  → {triggerEventDisplay}'
                </span>
              </Typography>
            }
            renderItem={(rule: RuleModel) => renderAlertCardItem(rule)}
          />
        )}
      </Stack>
      <Stack gap={1}>
        <SubsectionHeader
          title="Frequency"
          description="Choose how this rule fires — every time an event occurs, or only
            after it happens a set number of times."
          note="Note: frequency settings aren't shown in the impact preview."
        />
        <Grid container columnSpacing={2} size={{ xs: 10 }}>
          <Grid
            size={{ xs: 12 }}
            sx={{
              pl: 1,
              mt: 1,
              fontSize: 14,
              '& .MuiFormControlLabel-label, & .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiMenuItem-root':
                {
                  fontSize: 14,
                },
            }}
          >
            <Controller
              name="triggerInfo.occursEverytime"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value ? 'everyTime' : 'threshold'}
                  onChange={(e) => handleFrequencyChange(e, field.onChange)}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="everyTime"
                    control={<Radio />}
                    label="Every time the event occurs"
                    sx={{ width: 'fit-content' }}
                  />
                  <Stack direction="row" alignItems="flex-start" gap={1}>
                    <FormControlLabel
                      value="threshold"
                      control={<Radio />}
                      label="When an event occurs"
                      sx={{ mr: 0 }}
                    />
                    <Box sx={{ width: 90, pt: 0.2 }}>
                      <Controller
                        name="triggerInfo.triggerThreshold"
                        control={control}
                        render={({ field: thresholdField }) => (
                          <TextField
                            type="number"
                            label="Threshold"
                            {...thresholdField}
                            autoComplete="off"
                            error={!!errors.triggerInfo?.triggerThreshold}
                            helperText={
                              errors.triggerInfo?.triggerThreshold
                                ? errors.triggerInfo?.triggerThreshold.message
                                : ' '
                            }
                            disabled={occursEverytime}
                            onChange={(e) =>
                              thresholdField.onChange(
                                e.target.value
                                  ? Number(e.target.value)
                                  : e.target.value
                              )
                            }
                          />
                        )}
                      />
                    </Box>
                    <Typography pt={1.2}>times</Typography>
                    <Stack
                      direction="row"
                      sx={{ ml: 2, gap: 2, alignItems: 'flex-start' }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Controller
                          name="triggerInfo.hasTimeDuration"
                          control={control}
                          render={({ field: durationToggle }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...durationToggle}
                                  checked={durationToggle.value}
                                  onChange={(e) =>
                                    durationToggle.onChange(e.target.checked)
                                  }
                                  disabled={occursEverytime}
                                />
                              }
                              label="Within a time window of"
                              sx={{ mr: 0, pb: 1, alignItems: 'center' }}
                            />
                          )}
                        />
                      </Stack>
                      <Stack direction="row" alignItems="flex-start" gap={0.75}>
                        <Box sx={{ width: 100, pt: 0.2 }}>
                          <Controller
                            name="triggerInfo.triggerDuration"
                            control={control}
                            render={({ field: durationField }) => (
                              <TextField
                                type="number"
                                label="Duration"
                                {...durationField}
                                autoComplete="off"
                                error={!!errors.triggerInfo?.triggerDuration}
                                helperText={
                                  errors.triggerInfo?.triggerDuration
                                    ? errors.triggerInfo?.triggerDuration
                                        .message
                                    : ' '
                                }
                                disabled={occursEverytime || !hasTimeDuration}
                                onChange={(e) =>
                                  durationField.onChange(
                                    e.target.value
                                      ? Number(e.target.value)
                                      : e.target.value
                                  )
                                }
                              />
                            )}
                          />
                        </Box>
                        <Box
                          sx={{
                            width: 100,
                            pt: 0.2,
                          }}
                        >
                          <Controller
                            name="triggerInfo.triggerDurationType"
                            control={control}
                            render={({ field: durationTypeField }) => (
                              <TextField
                                label="Type"
                                select
                                {...durationTypeField}
                                sx={{
                                  width: '100%',
                                  '& .MuiInputBase-root': {
                                    height: 37,
                                  },
                                }}
                                helperText={' '}
                                autoComplete="off"
                                disabled={occursEverytime || !hasTimeDuration}
                              >
                                {durationTypeOptions.map((option) => (
                                  <MenuItem
                                    value={option.value}
                                    key={option.displayValue}
                                  >
                                    {option.displayValue}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Box sx={{ pl: 4 }}></Box>
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
      </Stack>

      <Stack>
        <SubsectionHeader
          title="Trigger Sources *"
          description="Filter this rule to specific parts of your organization. Add an
              org or domain filter, a Drive location filter, or both — the rule
              only fires when any sources are matched."
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Tabs
                value={sourceType}
                onChange={(_, value) => {
                  const updatedSourceType: RulesEngineSourceType = value;
                  if (updatedSourceType !== 'User' && watch('allSource')) {
                    setValue('allSource', false);
                    setValue('targetsExcluded', []);
                  }
                  setSourceType(updatedSourceType);
                }}
              >
                {allowedEntityList.map((entity) => (
                  <Tab key={entity} value={entity} label={entity} />
                ))}
              </Tabs>

              <Divider />

              <CardContent sx={{ flexGrow: 1 }}>
                <Controller
                  name={'triggerSources'}
                  control={control}
                  render={() => renderSourceAutocomplete()}
                />
                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {renderSourceChips()}
                </Stack>
                {isDriveUserEvent && (
                  <AllSourceOptions
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    handleAddAllUsersChecked={handleAddAllUsersChecked}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
          {currentTriggerEventOption.type === 'drive' ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Tabs
                  value={driveEntityType}
                  onChange={(_, value) => setDriveEntityType(value)}
                >
                  <Tab value="Shared Drives" label="Shared Drives" />
                  <Tab value="User Drives" label="User Drives" />
                  <Tab
                    value="Regular Expressions"
                    label="Regular Expressions"
                  />
                </Tabs>

                <Divider />

                <CardContent sx={{ flexGrow: 1 }}>
                  {renderDriveSourceSelector()}
                  <Stack
                    direction="row"
                    sx={{ minHeight: 50, gap: 1, mt: 1, flexWrap: 'wrap' }}
                  >
                    {renderDriveSourceChips()}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>

        {openDriveTreeDialog && userDriveAutoCompleteValue !== '' ? (
          <DriveTreeDialog
            open={openDriveTreeDialog}
            handleClose={() => {
              setOpenDriveTreeDialog(false);
              setUserDriveAutoCompleteValue('');
              autocompleteKey.current = new Date().getTime().toString();
            }}
            userEmail={userDriveAutoCompleteValue}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            onSelection={() => {
              handleUserDriveSelection(
                userDriveAutoCompleteValue,
                selectedFolder
              );
            }}
          />
        ) : null}
      </Stack>
    </Stack>
  );
}
